import React, { useMemo } from 'react';

import { CartCouponTypeEnum } from '@src/core/buyerApiClient';
import { useAppSelector } from '@src/core/hooks';
import { cartStateSelector } from '@src/features/cart/store/cart';
import useTranslate from '@src/i18n/useTranslate';
import useCurrencyFormat from '@src/hooks/useCurrencyFormat';
import CartPackageItemGroup from '../../Package/CartPackageItemGroup';
import { PurchaseResume, ResumeValue, SaleItemList, Total } from './styles';
import CartSaleItem from './CartItem';
import { getCartCostPreviewDiscountCurrencyInfo } from '@src/domains/coupons';
import { groupCartItemsByProductPackage } from '@src/domains/cart';
import CurrencySelector from '../CurrencySelector';
import CurrencyWarning from '../CurrencyWarning';

interface DrawerProps {
  showTotal?: boolean;
  showPlaceholderImage?: boolean;
}

export const CartSummary = (props: DrawerProps) => {
  const { showTotal = false, showPlaceholderImage = false } = props;

  const { i18n } = useTranslate();
  const { formatCurrencyInfo } = useCurrencyFormat();

  const { cart } = useAppSelector(cartStateSelector);

  const getItems = () => {
    return cart?.items ?? [];
  };

  const groupedSaleItems = useMemo(() => groupCartItemsByProductPackage(getItems()), [cart]);

  if (!cart) return null;

  return (
    <>
      <CurrencySelector />
      <CurrencyWarning />
      <SaleItemList>
        {groupedSaleItems.packages.map((packageItem, index) => (
          <CartPackageItemGroup
            key={`${packageItem.productPackage.name}-${index}`}
            productPackage={packageItem.productPackage}
            items={packageItem.items}
          />
        ))}

        {groupedSaleItems.nonPackageItems?.map((item) => (
          <CartSaleItem key={item.id} item={item} showPlaceholderImage={showPlaceholderImage} />
        ))}
      </SaleItemList>

      <PurchaseResume>
        <ResumeValue>
          <p>{i18n('direct_link_summary_subtotal')}</p>
          <p>{formatCurrencyInfo(cart!.costPreview.fullPartialAmountCurrencyInfo)}</p>
        </ResumeValue>

        {cart?.coupon ? (
          <ResumeValue>
            <p>{i18n('direct_link_summary_coupon')}</p>
            <p>{cart?.coupon.code}</p>
          </ResumeValue>
        ) : null}

        {cart?.coupon ? (
          <ResumeValue>
            <p>{i18n('direct_link_summary_discount')}</p>
            <p>
              {cart?.coupon?.type === CartCouponTypeEnum.Percentage ? (
                <span>{`(-${cart?.coupon.formattedDiscount})`}</span>
              ) : null}

              {formatCurrencyInfo(getCartCostPreviewDiscountCurrencyInfo(cart))}
            </p>
          </ResumeValue>
        ) : null}

        {showTotal ? (
          <Total>
            <p>{i18n('direct_link_summary_total')}</p>
            <p>{formatCurrencyInfo(cart!.costPreview.totalAmountCurrencyInfo)}</p>
          </Total>
        ) : null}
      </PurchaseResume>
    </>
  );
};

export default CartSummary;
